import { useState } from "react";
import {
  footerData,
  certificationsData,
  awardsData,
  logoUrl,
} from "@/mocks/homeData";
import { useTheme } from "@/hooks/useTheme";

function GlowButton({ children, href }: { children: React.ReactNode; href: string }) {
  return (
    <a
      href={href}
      className="relative inline-flex items-center gap-2.5 px-7 py-3.5 rounded-xl font-semibold text-sm text-white whitespace-nowrap overflow-hidden group transition-all duration-300 hover:scale-[1.02] hover:brightness-110"
      style={{
        background: "linear-gradient(135deg, #2563EB 0%, #7C3AED 50%, #F97316 100%)",
        boxShadow:
          "0 0 20px rgba(124,58,237,0.35), 0 0 40px rgba(37,99,235,0.15), inset 0 1px 0 rgba(255,255,255,0.15)",
      }}
    >
      <span className="relative z-10">{children}</span>
      <i className="ri-arrow-right-line relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
      {/* Glow overlay */}
      <span
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{
          background:
            "linear-gradient(135deg, rgba(255,140,0,0.25) 0%, transparent 60%)",
        }}
      />
    </a>
  );
}

function CertificationCard({
  cert,
}: {
  cert: {
    name: string;
    description: string;
    icon?: string;
    logo?: string;
    color: string;
    checkColor: string;
  };
}) {
  return (
    <div className="flex items-center gap-4 px-5 py-4 rounded-xl border border-primary-800/40 bg-primary-900/30 backdrop-blur-sm transition-all duration-300 hover:border-primary-700/50 hover:bg-primary-900/50">
      <div
        className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 border ${cert.color}`}
      >
        {cert.logo ? (
          <img
            src={cert.logo}
            alt={cert.name}
            className="w-7 h-7 object-contain"
          />
        ) : (
          <span className="w-6 h-6 flex items-center justify-center">
            <i className={`${cert.icon} text-xl`} />
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-background-100 font-heading">
            {cert.name}
          </span>
          <span className={`w-4 h-4 flex items-center justify-center ${cert.checkColor}`}>
            <i className="ri-checkbox-circle-fill text-sm" />
          </span>
        </div>
        <span className="text-xs text-background-500">{cert.description}</span>
      </div>
    </div>
  );
}

function AwardBadge({
  award,
}: {
  award: {
    name: string;
    subtitle: string;
    color: string;
    textColor: string;
    icon: string;
  };
}) {
  return (
    <div className="flex flex-col items-center gap-2 group cursor-default">
      <div
        className="w-[88px] h-[88px] rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
        style={{ background: award.color }}
      >
        <span className="w-8 h-8 flex items-center justify-center text-white/90">
          <i className={`${award.icon} text-2xl`} />
        </span>
      </div>
      <div className="text-center">
        <p className="text-xs font-bold text-background-100 leading-tight">
          {award.name}
        </p>
        <p className="text-[11px] text-background-500 leading-tight mt-0.5">
          {award.subtitle}
        </p>
      </div>
    </div>
  );
}

function ThemeToggle({ isDark, toggle }: { isDark: boolean; toggle: () => void }) {
  return (
    <button
      onClick={toggle}
      className="relative w-12 h-7 rounded-full transition-colors duration-300 flex items-center px-0.5"
      style={{
        background: isDark
          ? "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)"
          : "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
        border: isDark ? "1px solid rgba(148,163,184,0.2)" : "1px solid rgba(148,163,184,0.3)",
      }}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      <span
        className={`w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
          isDark ? "translate-x-5" : "translate-x-0"
        }`}
        style={{
          background: isDark ? "#fbbf24" : "#f59e0b",
          boxShadow: isDark
            ? "0 0 8px rgba(251,191,36,0.4)"
            : "0 0 8px rgba(245,158,11,0.3)",
        }}
      >
        {isDark ? (
          <svg className="w-3 h-3 text-amber-900" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 3a1 1 0 010 2 7 7 0 107 7 1 1 0 012 0 9 9 0 11-9-9z" />
          </svg>
        ) : (
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="5" />
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </span>
    </button>
  );
}

function LanguageDropdown() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("EN");

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs text-background-400 hover:text-background-200 transition-colors"
      >
        <span className="w-4 h-4 flex items-center justify-center">
          <i className="ri-global-line" />
        </span>
        <span>{lang}</span>
        <i className={`ri-arrow-down-s-line text-xs transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute bottom-full right-0 mb-2 w-32 bg-background-50 rounded-xl border border-background-200/70 shadow-xl shadow-foreground-950/8 py-1.5 z-50 animate-fade-in-up">
            {["EN", "ES", "FR", "DE"].map((l) => (
              <button
                key={l}
                onClick={() => {
                  setLang(l);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-xs transition-colors ${
                  lang === l
                    ? "text-accent-600 bg-accent-50/50 font-semibold"
                    : "text-foreground-600 hover:text-accent-600 hover:bg-accent-50/30"
                }`}
              >
                {l === "EN" && "🇺🇸 English"}
                {l === "ES" && "🇪🇸 Español"}
                {l === "FR" && "🇫🇷 Français"}
                {l === "DE" && "🇩🇪 Deutsch"}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Footer() {
  const { isDark, toggle } = useTheme();

  return (
    <footer
      className="relative text-background-200 border-t border-primary-800/40 overflow-hidden"
      style={{
        background: "linear-gradient(180deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)",
      }}
    >
      {/* Subtle grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      {/* ===== TOP CTA BAR ===== */}
      <div className="relative border-b border-primary-800/40">
        <div className="container-astro py-8 md:py-10">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            {/* Left: Logo + text aligned */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
          <a href="/" className="shrink-0">
                <img
                  src={logoUrl}
                  alt="AstroJal Technology"
                  className="w-[150px] md:w-[170px] h-auto object-contain"
                />
              </a>
              <div className="flex flex-col">
                <p className="text-base md:text-lg font-semibold text-background-100 font-heading">
                  Have a project in mind?
                </p>
                <p className="text-sm text-background-500 mt-0.5">
                  Laravel Experts Building Scalable Digital Products
                </p>
              </div>
            </div>

            {/* Right: CTA button */}
            <GlowButton href="/contact-us">
              Start a Conversation
            </GlowButton>
          </div>
        </div>
      </div>

      {/* ===== MAIN FOOTER CONTENT ===== */}
      <div className="relative container-astro py-14 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8">
          {/* Company info */}
          <div className="lg:col-span-4">
            <p className="text-sm text-background-400 leading-relaxed mb-6 max-w-sm">
              Laravel experts building scalable digital products for businesses worldwide. Based in Ahmedabad, India, serving clients across the USA, UK, Canada, Australia, UAE, Europe and India.
            </p>

            <div className="space-y-2.5 mb-6">
              <a
                href={`mailto:${footerData.contact.email}`}
                className="flex items-center gap-2.5 text-sm text-background-400 hover:text-accent-400 transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center text-accent-400/70">
                  <i className="ri-mail-line" />
                </span>
                {footerData.contact.email}
              </a>
              <a
                href={`tel:${footerData.contact.phone}`}
                className="flex items-center gap-2.5 text-sm text-background-400 hover:text-accent-400 transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center text-accent-400/70">
                  <i className="ri-phone-line" />
                </span>
                {footerData.contact.phone}
              </a>
              <div className="flex items-start gap-2.5 text-sm text-background-400">
                <span className="w-4 h-4 flex items-center justify-center text-accent-400/70 mt-0.5">
                  <i className="ri-map-pin-line" />
                </span>
                <span>{footerData.contact.address}</span>
              </div>
              <a
                href={footerData.contact.website}
                className="flex items-center gap-2.5 text-sm text-background-400 hover:text-accent-400 transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center text-accent-400/70">
                  <i className="ri-global-line" />
                </span>
                {footerData.contact.website.replace("https://", "")}
              </a>
            </div>

            <div className="flex items-center gap-3">
              {footerData.social.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-800/60 border border-primary-700/40 hover:bg-accent-500 hover:border-accent-500 text-background-400 hover:text-background-50 transition-all duration-300"
                  aria-label={s.label}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={s.icon} />
                  </span>
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 lg:gap-6">
              <div>
                <h4 className="text-sm font-bold text-background-50 mb-5 font-heading tracking-wide">
                  Company
                </h4>
                <ul className="flex flex-col gap-3">
                  {footerData.company.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        className="text-sm text-background-400 hover:text-accent-400 transition-colors"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-bold text-background-50 mb-5 font-heading tracking-wide">
                  Services
                </h4>
                <ul className="flex flex-col gap-3">
                  {footerData.services.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        className="text-sm text-background-400 hover:text-accent-400 transition-colors"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-bold text-background-50 mb-5 font-heading tracking-wide">
                  Solutions
                </h4>
                <ul className="flex flex-col gap-3">
                  {footerData.solutions.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        className="text-sm text-background-400 hover:text-accent-400 transition-colors"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-bold text-background-50 mb-5 font-heading tracking-wide">
                  Industries
                </h4>
                <ul className="flex flex-col gap-3">
                  {footerData.industries.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        className="text-sm text-background-400 hover:text-accent-400 transition-colors"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-bold text-background-50 mb-5 font-heading tracking-wide">
                  Resources
                </h4>
                <ul className="flex flex-col gap-3">
                  {footerData.resources.map((item) => (
                    <li key={item.label}>
                      <a
                        href={item.href}
                        className="text-sm text-background-400 hover:text-accent-400 transition-colors"
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ===== CERTIFICATIONS BANNER ===== */}
      <div className="relative border-y border-primary-800/40">
        <div className="container-astro py-8 md:py-10">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="h-px flex-1 max-w-[80px] bg-gradient-to-r from-transparent to-primary-700/40" />
            <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-background-500 font-heading">
              Trust &amp; Authority
            </h3>
            <div className="h-px flex-1 max-w-[80px] bg-gradient-to-l from-transparent to-primary-700/40" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6 max-w-3xl mx-auto">
            {certificationsData.map((cert) => (
              <CertificationCard key={cert.name} cert={cert} />
            ))}
          </div>
        </div>
      </div>

      {/* ===== AWARDS & ACCOLADES ===== */}
      <div className="relative container-astro py-12 md:py-16">
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-px bg-gradient-to-r from-transparent to-background-500/30" />
          <div className="w-6 h-px bg-background-500/50" />
          <h3 className="text-xs font-bold uppercase tracking-[0.15em] text-background-400 font-heading">
            Awards &amp; Accolades
          </h3>
          <div className="w-6 h-px bg-background-500/50" />
          <div className="w-10 h-px bg-gradient-to-l from-transparent to-background-500/30" />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-6 md:gap-8 max-w-3xl mx-auto">
          {awardsData.map((award) => (
            <AwardBadge key={award.name} award={award} />
          ))}
        </div>
      </div>

      {/* ===== BOTTOM BAR ===== */}
      <div className="relative border-t border-primary-800/40">
        <div className="container-astro py-5 md:py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            {/* Left: Copyright + links */}
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
              <p className="text-xs text-background-600">
                &copy; 2026 AstroJal Technology. All rights reserved.
              </p>
              <div className="flex items-center gap-4 text-xs text-background-600">
                <a href="/privacy-policy" className="hover:text-background-300 transition-colors">
                  Privacy Policy
                </a>
                <a href="/terms-and-conditions" className="hover:text-background-300 transition-colors">
                  Terms &amp; Conditions
                </a>
                <a href="/privacy-policy" className="hover:text-background-300 transition-colors">
                  Cookies
                </a>
              </div>
            </div>

            {/* Right: Changelog, Theme toggle, Language */}
            <div className="flex items-center gap-5 md:gap-6">
              {/* Change Log */}
              <a
                href="/blog"
                className="flex items-center gap-1.5 text-xs text-background-500 hover:text-accent-400 transition-colors"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-git-commit-line" />
                </span>
                <span className="hidden sm:inline">Blog</span>
              </a>

              {/* Theme Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-background-600 hidden sm:inline">
                  {isDark ? "Dark" : "Light"}
                </span>
                <ThemeToggle isDark={isDark} toggle={toggle} />
              </div>

              {/* Language */}
              <LanguageDropdown />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}